import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, RagBadge, Pill, Table, Empty, useLoad, Loading, act, statusTone } from "@/components/common";
import { ApprovalQueue } from "@/components/ApprovalQueue";
import Portfolio from "@/pages/Portfolio";
import Director from "@/pages/Director";
import Finance from "@/pages/Finance";
import Admin from "@/pages/Admin";
import Approvals from "@/pages/Approvals";

const taskCols = [
  { k: "name", h: "Task", r: (t) => <Link to={`/projects/${t.project_id}?tab=plan`} className="font-medium text-indigo-700 hover:underline">{t.name}</Link> },
  { k: "due", h: "Due" }, { k: "percent", h: "%", r: (t) => `${t.percent}%` }, { k: "status", h: "Status", r: (t) => <Pill tone={statusTone(t.status)}>{t.status}</Pill> },
];

function PMHome({ d }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {d.project_cards.map((p) => (
        <Panel key={p.id} testid={`pm-project-card-${p.code}`} title={<span>{p.code} · {p.name} <span className="ml-2 font-normal text-slate-400">{p.class} · {p.phase}</span></span>} action={<RagBadge value={p.rag?.overall} />}>
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">What to do next</div>
          <ul className="space-y-2">
            {p.next_actions.map((a, i) => (
              <li key={a.text}>
                <Link to={a.link || `/projects/${p.id}?tab=${a.tab}`} data-testid={`next-action-${p.code}-${i}`} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors">
                  <span>{a.text}</span><ArrowRight className="h-4 w-4 text-indigo-500" />
                </Link>
              </li>
            ))}
          </ul>
          {p.metrics && <div className="mt-4 flex gap-6 text-xs text-slate-500"><span>Budget <b className="text-slate-800">{money(p.metrics.budget, p.metrics.currency)}</b></span><span>EAC <b className="text-slate-800">{money(p.metrics.eac, p.metrics.currency)}</b></span><span>Var <b className={p.metrics.variance_pct > 5 ? "text-red-600" : "text-slate-800"}>{p.metrics.variance_pct}%</b></span></div>}
        </Panel>
      ))}
      {d.project_cards.length === 0 && <Empty text="You don't manage any projects yet" />}
    </div>
  );
}

function CoordinatorHome({ d }) {
  const remind = (uid, title, pid) => act(api.post("/reminders/send", { user_id: uid, title, project_id: pid }), "Reminder sent");
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Overdue tasks" testid="coord-overdue-tasks">
        <Table rows={d.overdue_tasks} empty="No overdue tasks" cols={[...taskCols.slice(0, 2), { k: "assignee_name", h: "Owner" },
          { k: "x", h: "", r: (t) => t.assignee_id && <Button size="sm" variant="outline" data-testid={`chase-task-${t.id}`} onClick={() => remind(t.assignee_id, `Overdue task: ${t.name}`, t.project_id)}>Chase</Button> }]} />
      </Panel>
      <Panel title="Overdue actions" testid="coord-overdue-actions">
        <Table rows={d.overdue_actions} empty="No overdue actions" cols={[{ k: "title", h: "Action" }, { k: "due", h: "Due" }, { k: "owner_name", h: "Owner" },
          { k: "x", h: "", r: (a) => <Button size="sm" variant="outline" data-testid={`chase-action-${a.id}`} onClick={() => remind(a.owner_id, `Overdue action: ${a.title}`, a.project_id)}>Chase</Button> }]} />
      </Panel>
      <Panel title="Timesheets to chase (last week)" testid="coord-timesheet-chase" className="lg:col-span-2">
        <Table rows={d.timesheet_chase} empty="All timesheets in" cols={[{ k: "user_name", h: "Team member" }, { k: "project", h: "Project" }, { k: "week", h: "Week" },
          { k: "x", h: "", r: (c) => <Button size="sm" variant="outline" data-testid={`chase-ts-${c.user_id}-${c.project_id}`} onClick={() => remind(c.user_id, `Timesheet missing: w/c ${c.week}`, c.project_id)}>Send reminder</Button> }]} />
      </Panel>
    </div>
  );
}

function TasksPanel({ d }) {
  return (
    <Panel title="Tasks due this week" testid="tasks-due-week" action={<Link to="/my-tasks" className="text-xs text-indigo-600" data-testid="open-my-tasks">All my tasks →</Link>}>
      <Table rows={d.due_this_week || []} cols={taskCols} empty="Nothing due this week" />
      <div className="mt-3 text-xs text-slate-500" data-testid="timesheet-week-status">Timesheet w/c {d.week}: {d.timesheets_this_week?.length ? "submitted" : <Link to="/my-tasks" className="font-semibold text-amber-600">not submitted — submit now</Link>}</div>
    </Panel>
  );
}

function ReportsPanel({ d }) {
  return (
    <Panel title="Latest status reports" testid="latest-reports">
      <div className="space-y-3">
        {(d.latest_reports || []).map((r) => (
          <Link key={r.id} to={`/reports/${r.id}`} className="block rounded-lg border border-slate-200 p-3 hover:border-indigo-300" data-testid={`report-link-${r.project_code}`}>
            <div className="flex items-center justify-between"><span className="font-medium">{r.project_code} · {r.project_name}</span><RagBadge value={r.rag?.overall} /></div>
            <div className="mt-1 text-sm text-slate-600">{r.summary}</div>
            <div className="mt-1 text-xs text-slate-400">Published {new Date(r.published_at).toLocaleDateString()} by {r.author}</div>
          </Link>
        ))}
        {!d.latest_reports?.length && <Empty text="No reports published yet" />}
      </div>
    </Panel>
  );
}

const TITLES = {
  pm: ["My Projects", "Your “what to do next” checklist for every project you run."],
  coordinator: ["Coordination board", "Overdue tasks, actions and timesheets to chase."],
  tech_lead: ["My workstreams", "Deliverables awaiting your technical review come first."],
  project_admin: ["Admin queue", "Access requests and unfiled documents."],
  team_member: ["My Tasks", "Tasks due this week and your timesheet."],
  stakeholder: ["Project overview", "Latest status reports and items for your acceptance."],
  psc: ["Executive view", "Items awaiting your endorsement or decision."],
};

export default function Home() {
  const { user } = useAuth();
  const role = user.role;
  const simple = !["pgm", "pmo", "cab", "finance", "sysadmin"].includes(role);
  const { data: d, error, reload } = useLoad(() => (simple ? api.get("/home").then((r) => r.data) : Promise.resolve(null)), [role]);
  if (role === "pgm") return <Director />;
  if (role === "pmo") return <Portfolio home />;
  if (role === "cab") return <Approvals />;
  if (role === "finance") return <Finance />;
  if (role === "sysadmin") return <Admin />;
  if (!d) return <Loading error={error} />;
  const [title, sub] = TITLES[role];
  return (
    <>
      <PageHeader overline={`Welcome, ${user.name.split(" ")[0]}`} title={title} subtitle={sub} />
      <Panel title={<span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Pending my action ({d.pending_approvals.length})</span>} testid="home-pending-approvals">
        <ApprovalQueue items={d.pending_approvals} onChange={reload} testid="home-approval-queue" />
      </Panel>
      {role === "pm" && <PMHome d={d} />}
      {role === "coordinator" && <CoordinatorHome d={d} />}
      {["tech_lead", "team_member"].includes(role) && <TasksPanel d={d} />}
      {role === "project_admin" && (
        <Panel title="Unfiled documents" testid="unfiled-docs">
          <Table rows={d.unfiled} empty="Everything is filed" cols={[{ k: "name", h: "Document" }, { k: "uploaded_by_name", h: "Uploaded by" },
            { k: "x", h: "", r: (x) => <Link className="text-indigo-600 text-sm" data-testid={`file-doc-link-${x.id}`} to={`/projects/${x.project_id}?tab=documents`}>File it →</Link> }]} />
        </Panel>
      )}
      {["stakeholder", "psc"].includes(role) && <ReportsPanel d={d} />}
      <Panel title="My projects" testid="home-projects">
        <Table rows={d.projects} cols={[{ k: "code", h: "Code" }, { k: "name", h: "Project", r: (p) => <Link to={`/projects/${p.id}`} className="font-medium text-indigo-700 hover:underline" data-testid={`home-project-${p.code}`}>{p.name}</Link> },
          { k: "class", h: "Class" }, { k: "phase", h: "Phase" }, { k: "rag", h: "RAG", r: (p) => <RagBadge value={p.rag?.overall} /> }]} />
      </Panel>
    </>
  );
}
